import produce from "immer"

const createRandomId = () => Math.floor(Math.random() * 9999999)

// Update order keys to match the current indexed order
export const updateOrdering = (items, orderKey = "order") => {
  return produce(items, (draft) => {
    draft.forEach((c, i) => {
      c[orderKey] = i + 1
    })
  })
}

// Enables keying the list items even when the items don't have a stable ID
// This is important:
// - drag and drop
// - preventing QuillEditor input from losing focus
export const getCurrentItemOrderIds = (items = [], currentOrderIds) => {
  return items
    ? items.map((item, i) => currentOrderIds[i] || createRandomId())
    : []
}

export const getNextItemOrderIds = (currentOrderIds, oldIndex, newIndex) => {
  let nextOrderIds = currentOrderIds.slice()
  // remove item
  if (typeof oldIndex === "number") {
    nextOrderIds = currentOrderIds.filter((id, i) => i !== oldIndex)
  }
  // add item to new index
  if (typeof newIndex === "number") {
    nextOrderIds.splice(
      newIndex,
      0,
      currentOrderIds[oldIndex] || createRandomId()
    )
  }
  return nextOrderIds
}

// Get next "order" value for a set
export const getNextOrder = (items) => {
  return items.length + 1
}

// Determine which item should be active after a change
const calcNextIndex = ({
  changeType,
  itemIndex = null,
  oldItemIndex = null,
  activeIndex,
  items
}) => {
  if (changeType === "sort") {
    // Preserve state on item being moved if selected
    if (activeIndex === oldItemIndex) {
      return itemIndex

      // Preserve selected state on item if replaced by moved item
    } else if (activeIndex === itemIndex) {
      const displacement = itemIndex < oldItemIndex ? 1 : -1
      return activeIndex + displacement

      // Preserve state on selected item if displaced by leftward move
    } else {
      let displacement = 0
      if (itemIndex < activeIndex && oldItemIndex > activeIndex)
        displacement = 1
      if (itemIndex > activeIndex && oldItemIndex < activeIndex)
        displacement = -1
      return activeIndex + displacement
    }
  } else if (changeType === "remove") {
    // If removing the selected tab, select adjacent
    if (oldItemIndex === activeIndex) {
      return oldItemIndex === 0 ? 0 : oldItemIndex - 1

      // If removing another tab, preserve selection
    } else {
      return oldItemIndex > activeIndex ? activeIndex : activeIndex - 1
    }
  } else if (changeType === "add") {
    return items.length - 1
  }

  return activeIndex
}

export const getNextActiveIndex = (
  changeType,
  itemIndex = null,
  oldItemIndex = null,
  collection = null,
  activeIndex
) => {
  const nextItems = collection ? collection : items

  return calcNextIndex({
    changeType,
    items: nextItems,
    activeIndex,
    itemIndex,
    oldItemIndex
  })
}

export const maybeApplyAutoAdd = (
  currentItems,
  { autoGenerateItem, generateItem, checkIsAutoGenerated, items: stateItems }
) => {
  if (autoGenerateItem) {
    const autoGeneratedItemInPayload = currentItems.find((item) =>
      checkIsAutoGenerated(item)
    )

    // If the payload contains an auto-generated item, we don't need to add one
    if (autoGeneratedItemInPayload) {
      return currentItems
    }

    // Use state auto-generated item if it exists
    // const stateAutoGeneratedItem = stateItems.find(item =>
    //   checkIsAutoGenerated(item)
    // )

    // if (stateAutoGeneratedItem ) {
    //   const itemValueExists = !!currentItems.find(item => item.value === stateAutoGeneratedItem.value)
    //   return [
    //     ...currentItems,
    //     {
    //       ...stateAutoGeneratedItem,
    //       order: currentItems.length + 1,
    //       // Need to refresh the value to prevent duplicate key errors
    //       value: itemValueExists ? createID() : stateAutoGeneratedItem.value
    //     }
    //   ]
    // }

    // If neither state or payload contain an auto-generated item, we need to add one
    return [
      ...currentItems,
      generateItem({ order: getNextOrder(currentItems) })
    ]
  }

  return currentItems
}
